import React, { MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { RowActionsRender, TableColumn, TableRowData } from '@inplayer-org/inplayer-ui';

// types
import { DonationFee } from 'ducks/merchantDucks/items/types';
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// selectors
import { getDonationOptions } from 'ducks/merchantDucks/items/selectors';

// components
import ConfirmModal from 'components/Modals/ConfirmModal';
import GenericTable from 'components/Table/GenericTable';
import CommonIcon from 'components/CommonIcon';
import { Wrapper } from 'pages/MerchantPages/Assets/Price/components/PricesTable';

interface Props {
  handleDeleteModal: (modal: any) => void;
  handleEditDonation: (donation: any) => void;
  deleteDonationPrice: (donationId: number) => () => void;
  isDonationFeatureEnabled: boolean;
}

type DonationFeeItem = Omit<DonationFee, 'id'>;

interface DonationTableData extends TableRowData, DonationFeeItem {}

const DonationsTable: React.FC<Props> = ({
  handleDeleteModal,
  handleEditDonation,
  isDonationFeatureEnabled,
  deleteDonationPrice,
}) => {
  const donationOptions = useSelector(getDonationOptions);

  const [donationToDelete, setDonationToDelete] = useState(-1);

  const generateColumns = (): Array<TableColumn<DonationTableData>> => [
    {
      title: 'Donation description',
      key: 'description',
    },
    {
      title: 'Amount',
      key: 'float_amount',
      render: ({ rowValues }) => {
        const { amount, currency } = rowValues;
        return <>{`${amount} ${currency}`}</>;
      },
    },
  ];

  const showModal = (donationId: number) => (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (isDonationFeatureEnabled) {
      handleDeleteModal(ModalNames.DeleteAssetPrice);
      setDonationToDelete(donationId);
    }
  };

  const renderRowActions: RowActionsRender<DonationTableData> = ({ row }) => {
    return (
      <>
        <span>
          <CommonIcon
            tag={`button_edit_${row.id}`}
            isIconDisabled={!isDonationFeatureEnabled}
            hoverStatus="edit"
            name="edit"
            onClick={() => handleEditDonation(row)}
          />
        </span>
        <span>
          <CommonIcon
            tag={`button_delete_${row.id}`}
            isIconDisabled={!isDonationFeatureEnabled}
            hoverStatus="danger"
            name="delete"
            onClick={showModal(Number(row.id))}
          />
        </span>
      </>
    );
  };

  return (
    <Wrapper>
      <GenericTable<DonationTableData>
        columns={generateColumns()}
        data={donationOptions}
        showLoader={false}
        options={{
          rowActions: renderRowActions,
        }}
        type="donations"
        actionsRowTitle="Edit"
      />
      <ConfirmModal
        modalName={ModalNames.DeletePriceDonation}
        handleConfirm={deleteDonationPrice(donationToDelete)}
        message="Are you sure you want to delete this donation?"
      />
    </Wrapper>
  );
};

export default DonationsTable;
